import { supabase } from "@/integrations/supabase/client";

export type AdminListing = {
  id: string;
  user_id: string;
  title: string;
  price: number;
  category: string;
  city: string;
  status: string;
  badge: string;
  badge_expires_at: string | null;
  views: number;
  images: string[];
  reject_reason: string | null;
  created_at: string;
  specs?: Record<string, string> | null;
};

export type AdminUser = {
  id: string;
  full_name: string | null;
  phone: string | null;
  blocked: boolean;
  created_at: string;
  is_admin: boolean;
  is_moderator: boolean;
  listings: number;
  store_id: string | null;
  store_verified: boolean;
  verified: boolean;
};

const LISTING_COLUMNS =
  "id, user_id, title, price, category, city, status, badge, badge_expires_at, views, images, reject_reason, created_at, specs";

export async function isAdmin(userId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
  if (error) return false;
  return Boolean(data);
}

export async function fetchAllListings(): Promise<AdminListing[]> {
  const { data, error } = await supabase
    .from("listings")
    .select(LISTING_COLUMNS)
    .order("created_at", { ascending: false })
    .limit(500);
  if (error) throw error;
  return (data ?? []) as AdminListing[];
}

export async function setListingStatus(id: string, status: string, rejectReason?: string) {
  const { error } = await supabase
    .from("listings")
    .update({ status, reject_reason: status === "rejected" ? (rejectReason ?? null) : null })
    .eq("id", id);
  if (error) throw error;
  await logAdminAction("listing_status", "listings", id, { status, reason: rejectReason ?? null });
}

/**
 * Admin elana balansdan asılı olmayaraq VIP/Premium paketi verir və ya ləğv edir.
 * days=0 və ya badge="none" → paket ləğv olunur.
 */
export async function setListingBadge(id: string, badge: "none" | "vip" | "premium", days = 7) {
  const expires =
    badge === "none" || days <= 0
      ? null
      : new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
  const { error } = await supabase
    .from("listings")
    .update({
      badge: badge === "none" ? "none" : badge,
      badge_expires_at: expires,
      badge_price: 0,
    })
    .eq("id", id);
  if (error) throw error;
  await logAdminAction("listing_badge", "listings", id, { badge, days });
}


export type ListingFlag = "vip" | "premium" | "store";
const FLAG_KEYS: Record<ListingFlag, string> = {
  vip: "_admin_vip",
  premium: "_admin_premium",
  store: "_admin_store",
};

/** Admin elanın üzərində VIP / Premium / Mağaza göstəricisini yandırıb-söndürür. */
export async function setListingFlag(id: string, flag: ListingFlag, on: boolean) {
  const { data, error: readErr } = await supabase
    .from("listings")
    .select("specs")
    .eq("id", id)
    .maybeSingle();
  if (readErr) throw readErr;
  const specs = { ...(((data as { specs?: Record<string, string> } | null)?.specs) ?? {}) };
  if (on) specs[FLAG_KEYS[flag]] = "1";
  else delete specs[FLAG_KEYS[flag]];
  const { error } = await supabase.from("listings").update({ specs }).eq("id", id);
  if (error) throw error;
  await logAdminAction("listing_flag", "listings", id, { flag, on });
}

export const hasListingFlag = (specs: Record<string, string> | null | undefined, flag: ListingFlag) =>
  (specs ?? {})[FLAG_KEYS[flag]] === "1";

/** Konkret istifadəçinin bütün elanları (admin üçün). */
export async function fetchListingsByUser(userId: string): Promise<AdminListing[]> {
  const { data, error } = await supabase
    .from("listings")
    .select(LISTING_COLUMNS)
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as AdminListing[];
}


export async function deleteListing(id: string) {
  const { error } = await supabase.from("listings").delete().eq("id", id);
  if (error) throw error;
}

export async function fetchAllUsers(): Promise<AdminUser[]> {
  const [{ data: profiles, error }, { data: roles }, { data: listings }, { data: stores }] = await Promise.all([
    supabase
      .from("profiles")
      .select("id, full_name, phone, blocked, created_at, verified")
      .order("created_at", { ascending: false })
      .limit(500),
    supabase.from("user_roles").select("user_id, role").in("role", ["admin", "moderator"]),
    supabase.from("listings").select("user_id").limit(2000),
    supabase.from("stores").select("id, owner_id, verified, status").limit(1000),
  ]);
  if (error) throw error;
  const adminIds = new Set((roles ?? []).filter((r) => r.role === "admin").map((r) => r.user_id));
  const moderatorIds = new Set((roles ?? []).filter((r) => r.role === "moderator").map((r) => r.user_id));
  const counts = new Map<string, number>();
  for (const l of listings ?? []) counts.set(l.user_id, (counts.get(l.user_id) ?? 0) + 1);
  const storeByOwner = new Map<string, { id: string; verified: boolean }>();
  for (const s of stores ?? []) {
    if (s.owner_id) storeByOwner.set(s.owner_id, { id: s.id, verified: Boolean(s.verified) });
  }
  return (profiles ?? []).map((p) => ({
    ...p,
    is_admin: adminIds.has(p.id),
    is_moderator: moderatorIds.has(p.id),
    listings: counts.get(p.id) ?? 0,
    store_id: storeByOwner.get(p.id)?.id ?? null,
    store_verified: storeByOwner.get(p.id)?.verified ?? false,
  })) as AdminUser[];
}

/** İstifadəçini təsdiqlənmiş mağazaya çevirir və ya mağaza statusunu ləğv edir. */
export async function setUserStore(user: AdminUser, enabled: boolean) {
  if (!enabled) {
    if (!user.store_id) return;
    const { error } = await supabase.from("stores").delete().eq("id", user.store_id);
    if (error) throw error;
    return;
  }
  if (user.store_id) {
    const { error } = await supabase
      .from("stores")
      .update({ status: "active", verified: true })
      .eq("id", user.store_id);
    if (error) throw error;
    return;
  }
  const name = user.full_name?.trim() || "Mağaza";
  const slug = `${name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")}-${user.id.slice(0, 6)}`;
  const { error } = await supabase.from("stores").insert({
    owner_id: user.id,
    name,
    slug,
    phone: user.phone,
    status: "active",
    verified: true,
  });
  if (error) throw error;
}

/** Satıcıya "Yoxlanmış satıcı" nişanı verir və ya götürür. */
export async function setUserVerified(userId: string, verified: boolean) {
  const { error } = await supabase.from("profiles").update({ verified }).eq("id", userId);
  if (error) throw error;
}

export async function setUserBlocked(userId: string, blocked: boolean) {
  const { error } = await supabase.from("profiles").update({ blocked }).eq("id", userId);
  if (error) throw error;
}

export async function setUserAdmin(userId: string, admin: boolean) {
  if (admin) {
    const { error } = await supabase.from("user_roles").insert({ user_id: userId, role: "admin" });
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from("user_roles")
      .delete()
      .eq("user_id", userId)
      .eq("role", "admin");
    if (error) throw error;
  }
}

export async function setUserModerator(userId: string, moderator: boolean) {
  if (moderator) {
    const { error } = await supabase.from("user_roles").insert({ user_id: userId, role: "moderator" });
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from("user_roles")
      .delete()
      .eq("user_id", userId)
      .eq("role", "moderator");
    if (error) throw error;
  }
}

/* ---------------- Ümumi köməkçilər ---------------- */

type Row = Record<string, unknown>;

export async function adminList(
  table:
    | "stores"
    | "transactions"
    | "reports"
    | "push_notifications"
    | "reviews"
    | "banners"
    | "categories"
    | "cities"
    | "brands"
    | "brand_models"
    | "promo_codes"
    | "admin_audit_log"
    | "user_roles"
    | "site_settings",
  orderColumn = "created_at",
  ascending = false,
): Promise<Row[]> {
  const { data, error } = await supabase
    .from(table)
    .select("*")
    .order(orderColumn, { ascending })
    .limit(500);
  if (error) throw error;
  return (data ?? []) as Row[];
}

export async function adminInsert(table: string, values: Row) {
  const { error } = await supabase.from(table as never).insert(values as never);
  if (error) throw error;
}

export async function adminUpdate(table: string, id: string, values: Row, key = "id") {
  const { error } = await supabase
    .from(table as never)
    .update(values as never)
    .eq(key, id);
  if (error) throw error;
}

export async function adminDelete(table: string, id: string, key = "id") {
  const { error } = await supabase.from(table as never).delete().eq(key, id);
  if (error) throw error;
}

export async function adjustBalance(
  userId: string,
  amount: number,
  note: string,
  kind?: string,
) {
  return adjustBalanceInternal(userId, amount, note, kind);
}

/** Admin əməliyyatlarını audit jurnalına yazır (səssiz — uğursuzluq axını dayandırmır). */
export async function logAdminAction(
  action: string,
  entity: string,
  entityId?: string,
  details: Record<string, string | number | boolean | null> = {},
) {
  const { data } = await supabase.auth.getUser();
  const adminId = data.user?.id;
  if (!adminId) return;
  await supabase
    .from("admin_audit_log")
    .insert({ admin_id: adminId, action, entity, entity_id: entityId ?? null, details })
    .then(() => undefined, () => undefined);
}

async function adjustBalanceInternal(
  userId: string,
  amount: number,
  note: string,
  kind?: string,
) {
  const { data, error } = await supabase
    .from("profiles")
    .select("balance")
    .eq("id", userId)
    .maybeSingle();
  if (error) throw error;
  const next = Number(data?.balance ?? 0) + amount;
  const { error: uErr } = await supabase.from("profiles").update({ balance: next }).eq("id", userId);
  if (uErr) throw uErr;
  await adminInsert("transactions", {
    user_id: userId,
    amount,
    kind: kind ?? (amount >= 0 ? "topup" : "withdraw"),
    status: "completed",
    method: "admin",
    note,
  });
  await logAdminAction("balance_adjust", "profiles", userId, { amount, note });
  return next;
}
